/******************************************************************************
 *** BgtSpcDpstMng ** 특별회계기금 수입 결의서 관리
*******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";
import BgtSpcDpstMngList from "./BgtBrDpstMngList";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import TextBox from "@/js/common/ui/input/TextBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import Button from "@/js/common/ui/Button";
import { budgetFilesProps } from "@/js/common/types/types";
import BudgetFile from "../budgetFileBox/BudgetFile";
import Axios from "@/js/common/Axios";
import AtrzLineBox from "../bgtovrl/dialog/AtrzLineBox";

interface Props {
    systemCode: string;
}
const BgtSpcDpstMng = ( {systemCode}: Props ) => {
    const userInfo = useRecoilValue(Atom.userInfo);

    // 검색 조건 
    const [upOgnzCd  , setUpOgnzCd  ] = React.useState<string>(userInfo.upOgnzCd);      // 상위조직코드(본부)
    const [ognzCd    , setOgnzCd    ] = React.useState<string>(userInfo.ognzCd  );      // 조직코드(지부)
    const [acntgYear , setAcntgYear ] = React.useState<string>(Util.getNowAcntgYear()); // 회계연도
    const [searchAtrzSttsCd, setSearchAtrzSttsCd] = React.useState<string>("");                     // 진행상태

    // 좌측 입력폼 
    const [spcAcntgDpstDocId, setSpcAcntgDpstDocId] = React.useState<string>(""); // 수입결의서 문서번호
    const [dpstRsdocNo      , setDpstRsdocNo      ] = React.useState<string>(""); // 결의서 문서번호
    const [dpstDt           , setDpstDt           ] = React.useState<string>(""); // 수입일자 (YYYYMMDD)
    const [bgtSbjct1stCd    , setBgtSbjct1stCd    ] = React.useState<string>(""); // 1차 코드(관)
    const [bgtSbjct2stCd    , setBgtSbjct2stCd    ] = React.useState<string>(""); // 2차 코드(항)
    const [bgtSbjct3stCd    , setBgtSbjct3stCd    ] = React.useState<string>(""); // 3차 코드(목)
    const [abstrCn          , setAbstrCn          ] = React.useState<string>(""); // 적요
    const [memo             , setMemo             ] = React.useState<string>(""); // 메모
    const [dpstAmt          , setDpstAmt          ] = React.useState<string>(""); // 수입 금액
    const [atrzSttsCd       , setAtrzSttsCd       ] = React.useState<string>("00"); // 결재상태정보
    const [atrzOpnnCn       , setAtrzOpnnCn       ] = React.useState<string>(""); // 반려사유(결재의견)
    const [listLine       , setListLine           ] = React.useState<any[]>([]);  // 결재 라인 정보
    const [openLine       , setOpenLine           ] = React.useState<boolean>(false); // 기본 펼침/접힘


    const [spcDpstFiles     , setSpcDpstFiles     ] = React.useState<File[]>([]); // 저장할 파일
    const [savedSpcDpstFiles, setSavedSpcDpstFiles] = React.useState<budgetFilesProps[]>([]); // 저장된 파일

    const [callTime, setCallTime] = React.useState<Date>(new Date()); // 검색버튼 트리거용

    // 특별회계기금 타이틀 설정
    const [systemName, setSystemName] = React.useState<string>("");
    const [fileSeCd, setFileSeCd] = React.useState(""); // 파일 구분값(E01, F01, G01)

    // 특별회계기금 수입결의서 상세정보 조회
    React.useEffect(() => {
        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/spc/dpst/select-bgt-spc-dpst-dtl.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                spcAcntgDpstDocId: spcAcntgDpstDocId   // 수입결의서 문서 ID
            },
            onSuccessFn: (res: any) => {
                if (Util.isNotEmpty(res.result.item)) {
                    setDpstRsdocNo  (res.result.item.dpstRsdocNo  ); // 수입결의서 문서번호
                    setDpstDt       (res.result.item.dpstDt       ); // 수입일자
                    setBgtSbjct1stCd(res.result.item.bgtSbjct1stCd); // 1차 코드(관)
                    setBgtSbjct2stCd(res.result.item.bgtSbjct2stCd); // 2차 코드(항)
                    setBgtSbjct3stCd(res.result.item.bgtSbjct3stCd); // 3차 코드(목)
                    setAbstrCn      (res.result.item.abstrCn      ); // 적요
                    setMemo         (res.result.item.memo         ); // 메모
                    setDpstAmt      (res.result.item.dpstAmt      ); // 수입금액
                    setAtrzSttsCd   (res.result.item.atrzSttsCd   ); // 결재진행상태
                    setAtrzOpnnCn   (res.result.item.atrzOpnnCn   ); // 반려사유(결재의견)
                    setListLine     (res.result.listLine          );  // 결재 라인리스트

                    setSavedSpcDpstFiles(res.result.listFile); // 첨부파일
                }
                AppContext.hideSpinner();
            },
        });
    }, [spcAcntgDpstDocId]);

    // 특별회계기금 타이틀 설정
    React.useEffect(() => {
        if (systemCode === "E1") {
            setSystemName("지부 운영비");
            setFileSeCd("E01");
            setBgtSbjct1stCd("I_06_001");
            setBgtSbjct2stCd("I_06_001_001");
        } else if (systemCode === "F1") {
            setSystemName("지부장 직무활동비");
            setFileSeCd("F01");
            setBgtSbjct1stCd("I_07_001");
            setBgtSbjct2stCd("I_07_001_001");
        } else if (systemCode === "G1") {
            setSystemName("지부 대회비");
            setFileSeCd("G01");
            setBgtSbjct1stCd("I_08_001");
            setBgtSbjct2stCd("I_08_001_001");
        }
    }, [systemCode]);

    // 특별회계기금 수입결의서 저장
    function doSave(atrzSttsCd: string) {
        let msgText = "임시저장";

        // 수입일자 확인
        if (Util.isEmpty(dpstDt)) {
            AppContext.openAlert({
                title: "수입일자 확인",
                msg: "수입일자를 입력하세요.",
                icon: "warning"
            });
            return;
        }

        // 작성완료 필수입력 확인
        if (atrzSttsCd === "02") {
            if (Util.isEmpty(bgtSbjct1stCd)) { AppContext.openAlert({ title: "예산과목 관 확인", msg: "예산과목 관 항목을 선택하세요.", icon: "warning" }); return; }
            if (Util.isEmpty(bgtSbjct2stCd)) { AppContext.openAlert({ title: "예산과목 항 확인", msg: "예산과목 항 항목을 선택하세요.", icon: "warning" }); return; }
            if (Util.isEmpty(bgtSbjct3stCd)) { AppContext.openAlert({ title: "예산과목 목 확인", msg: "예산과목 목 항목을 선택하세요.", icon: "warning" }); return; }
            if (Util.isEmpty(abstrCn      )) { AppContext.openAlert({ title: "적요 확인"       , msg: "적요를 입력하세요."            , icon: "warning" }); return; }
            if (Util.isEmpty(dpstAmt      )) { AppContext.openAlert({ title: "금액 확인"       , msg: "금액을 입력하세요."            , icon: "warning" }); return; }
            msgText = "작성완료";
        }

        AppContext.openAlert({
            title: "확인",
            msg: "수입결의서를 " + msgText + "하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: msgText,
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/spc/dpst/save-bgt-spc-dpst.do",
                    methodType: "post",
                    isMultipart: true,
                    paramData: {
                        spcAcntgDpstDocId: spcAcntgDpstDocId    , // 수입결의서 문서번호
                        spcAcntgSeCd     : systemCode           , // 시스템 구분 코드
                        orgCd            : ognzCd               , // 조직코드
                        dpstDt           : dpstDt               , // 수입일자 (YYYYMMDD)
                        acntgYear        : Util.getAcntgYear(dpstDt), // 회계연도
                        bgtSbjct1stCd    : bgtSbjct1stCd        , // 1차 코드(관)
                        bgtSbjct2stCd    : bgtSbjct2stCd        , // 2차 코드(항)
                        bgtSbjct3stCd    : bgtSbjct3stCd        , // 3차 코드(목)
                        abstrCn          : abstrCn              , // 적요
                        memo             : memo                 , // 메모
                        dpstAmt          : Util.uncomma(dpstAmt) === "" ? 0 : Number(Util.uncomma(dpstAmt)), // 수입 금액
                        atrzSttsCd       : atrzSttsCd           , // 상태정보
                        budgetFiles      : spcDpstFiles         , // 첨부파일
                        fileSeCd         : fileSeCd             , // 파일구분코드(특별회계기금_수입결의서[B01])
                    },
                    onSuccessFn: (res: any) => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "수입결의서 저장",
                            msg: "수입결의서가 저장되었습니다.",
                            icon: "check"
                        });
                        // 지출결의서 Insert 후 문서 ID 설정
                        if (Util.isEmpty(spcAcntgDpstDocId)) {
                            setSpcAcntgDpstDocId(res.result.spcAcntgDpstDocId); // 지출결의서 문서 ID
                        }
                        // 작성완료인 경우
                        if (atrzSttsCd === "02") {
                            doReset(); // 초기화
                        }
                        setCallTime(new Date());
                    },
                });
            },
        });
    }

    // 초기화
    function doReset() {
        // 좌측 입력폼
        setSpcAcntgDpstDocId(""); // 수입결의서 문서ID
        setDpstRsdocNo      (""); // 수입결의서 문서번호
        setDpstDt           (""); // 수입일자 (YYYYMMDD)
        setBgtSbjct3stCd    (""); // 3차 코드(목)
        setAbstrCn          (""); // 적요
        setMemo             (""); // 메모
        setDpstAmt          (""); // 수입 금액
        setAtrzSttsCd       ("00"); // 상태정보
        // 첨부파일
        setSpcDpstFiles     ([]); // 저장할 파일
        setSavedSpcDpstFiles([]); // 저장된 파일
    }

    // 문서 ID 변경 시 드롭존 초기화
    React.useEffect(() => {
        setSpcDpstFiles([]);
    }, [spcAcntgDpstDocId]);

    // 삭제
    function doDelete() {
        AppContext.openAlert({
            title: "확인",
            msg: "수입결의서를 삭제하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "삭제",
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/spc/dpst/delete-bgt-spc-dpst.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        spcAcntgDpstDocId: spcAcntgDpstDocId, // 수입결의서 문서 ID
                    },
                    onSuccessFn: () => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "수입결의서 삭제",
                            msg: "수입결의서가 삭제되었습니다.",
                            icon: "check"
                        });
                        doReset(); // 초기화
                        setCallTime(new Date());
                    },
                });
            },
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">{systemName} 수입결의서 관리</span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 조건 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    {/* 소속 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox value={upOgnzCd}
                            width={150} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox value={ognzCd}
                            width={180} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 회계연도 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox value={acntgYear}
                            width={120} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff" paddingRight={8}
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    {/* 진행상태 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">진행상태</span>
                        <SelectBox  value={searchAtrzSttsCd}
                            width={140} height={36}
                            fontSize={14}
                            fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff"
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("UI_ATRZ_STTS_CD_01"))}
                            placeholder="전체" paddingRight={8}
                            onChangeFunc={(data: string) => setSearchAtrzSttsCd(data)}
                        />
                    </div>

                    <input type="hidden" value={atrzSttsCd} name="atrzSttsCd" />


                    {/* 버튼 */}
                    {/* <div className="dpFlx aiC mlA gap8">
                        <Button buttonName="검색"
                            width={70} height={40}
                            fontFamily="fm-b" fontSize={16} letterSpacing={-1} color="#fff"
                            backgroundColor="#007bff" borderRadius={5}
                            buttonClass="dpFlx aiC jcC"
                            onClickFunc={() => setCallTime(new Date())}
                        />
                    </div> */}
                </div>
            </div>

            {/* 좌/우 */}
            <div className="dpFlx gap14 aiFs">
                {/* LEFT */}
                <div className="w360">
                    <div className="bd1d9d9d9 bdrd6 bgcWhite p15">
                        <div className="mb10">
                            <span className="fm-b fs16 fc007bff ls1 fw700">수입결의서 입력</span>
                            <span className="fm-b fs13 ls06 fc1A5276" style={{marginLeft: "77px"}}>{dpstRsdocNo}</span>
                        </div>

                        {atrzSttsCd === "05" && (
                        <AtrzLineBox
                            listLine={listLine}
                            open={openLine}
                            onToggle={() => setOpenLine((v) => !v)}
                        />)}

                        {/* 수입일자 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">수입일자</span>
                            </div>
                            <DatePickerBox value={dpstDt}
                                width={320} height={36}
                                fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                bgColor={["01", "02", "05"].includes(atrzSttsCd) ? "#F6F6F6": "white"}
                                textAlign="left"
                                calendarThemeColor={AppContext.themeColor}
                                onChange={(data: string) => setDpstDt(data)}
                                disabled={["01", "02", "05"].includes(atrzSttsCd) ? true : false}   // 작성중(01), 작성완료(02), 반려(05)
                            />
                        </div>

                        {/* 관/항/목 */}
                        <div className="dpFlx mb10">
                            {/* 관 */}
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">관</span>
                                </div>
                                <SelectBox value={bgtSbjct1stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#f6f6f6" paddingRight={8}
                                    codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt1CodeArray(systemCode, "01"))}
                                    placeholder="선택"
                                    disabled={true}
                                    onChangeFunc={(data: string) => setBgtSbjct1stCd(data)}
                                />
                            </div>

                            {/* 항 */}
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">항</span>
                                </div>
                                <SelectBox value={bgtSbjct2stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#f6f6f6" paddingRight={8}
                                    codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt2CodeArray(systemCode, bgtSbjct1stCd))}
                                    placeholder="선택"
                                    disabled={true}
                                    onChangeFunc={(data: string) => setBgtSbjct2stCd(data)}
                                    />
                            </div>

                            {/* 목 */}
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">목</span>
                                </div>
                                <SelectBox value={bgtSbjct3stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#fff" paddingRight={8}
                                    codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt3CodeArray(systemCode, bgtSbjct2stCd))}
                                    placeholder="선택"
                                    onChangeFunc={(data: string) => setBgtSbjct3stCd(data)}
                                />
                            </div>
                        </div>


                        {/* 적요 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">적요</span>
                            </div>
                            <TextBox value={abstrCn}
                                width={320} height={36}
                                fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                placeholder="수입 관련 적요를 입력하세요"
                                style={{ backgroundColor: "#fff", padding: "0 12px" }}
                                onChangeFunc={(t: string) => setAbstrCn(t)}
                            />
                        </div>

                        {/* 메모 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">메모</span>
                            </div>
                            <TextBox value={memo}
                                width={320} height={36}
                                fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                placeholder="수입 관련 메모를 입력하세요"
                                style={{ backgroundColor: "#fff", padding: "0 12px" }}
                                onChangeFunc={(t: string) => setMemo(t)}
                            />
                        </div>

                        {/* 금액 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">금액</span>
                            </div>
                            <div className="w320 h36 bgcWhite dpFlx aiC bsBdb">
                                <NumberTextBox value={dpstAmt}
                                    className="w320 h36 bd1 bdrd2"
                                    thousandsGroupStyle="thousand" thousandSeparator=","
                                    placeholder="0"
                                    fontSize={14} fontFamily="fm-c" letterSpacing={-1}
                                    onChange={(data: string) => setDpstAmt(data)}
                                    inputClass={"taR mr10"}
                                />
                            </div>
                        </div>

                        <BudgetFile
                            atrzSttsCd={atrzSttsCd}     // 결재상태코드
                            fileSeCd={fileSeCd}         // 파일구분코드(특별회계기금_수입결의서[B01])
                            savedBudgetFiles   ={savedSpcDpstFiles}
                            setSavedBudgetFiles={setSavedSpcDpstFiles}
                            budgetFiles        ={spcDpstFiles}
                            setBudgetFiles     ={setSpcDpstFiles}
                        />

                        {/* 반려 */}
                        {atrzSttsCd === "05" &&
                            <div className="dpFlx flxdCol">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">반려</span>
                                </div>
                                <div className="dpFlx aiFs">
                                    <div className="flxg1 bsBdb">
                                        <textarea
                                            value={atrzOpnnCn}
                                            className="w320 h70 bsBdb p10 bd1 bdcDFDFDF bdrd2 fm-c fs14 fc333333 ls1 bgcWhite rszN"
                                            maxLength={2000}
                                            placeholder="반려 사유를 상세히 입력해주세요."
                                            onChange={(e) => setAtrzOpnnCn(e.target.value)}
                                            readOnly={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        }

                        <div className="bdb1 bdcDDDDDD mt15 mb15" />

                        <div className="dpFlx jcSb aiC gap10">
                            <Button buttonName="초기화"
                                width={80} height={40}
                                fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                variant="styInitBtn" borderRadius={6}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={() => doReset()}
                            />

                            {(atrzSttsCd === "01" || atrzSttsCd === "02" || atrzSttsCd === "05") &&
                                <Button buttonName="삭제"
                                    width={75} height={40}
                                    fontFamily="fm-b" fontSize={15} letterSpacing={-1}
                                    variant="styDeleteBtn" borderRadius={6}
                                    buttonClass="dpFlx aiC jcC"
                                    onClickFunc={doDelete}
                                />
                            }

                            {(atrzSttsCd === "00" || atrzSttsCd === "01") &&
                                <Button buttonName="임시저장"
                                    width={100} height={40}
                                    fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                    variant="styTempBtn" borderRadius={6}
                                    buttonClass="dpFlx aiC jcC"
                                    onClickFunc={() => doSave("01")}
                                />
                            }

                            <Button buttonName="작성완료"
                                width={100} height={40}
                                fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                variant="styCompleteBtn" borderRadius={6}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={() => doSave("02")}
                            />
                        </div>
                    </div>
                </div>

                {/* RIGHT 수입결의서 목록*/}
                <div className="flx1">
                    <BgtSpcDpstMngList
                        ognzCd  ={ognzCd}
                        acntgYear={acntgYear}
                        systemCode={systemCode}
                        searchAtrzSttsCd={searchAtrzSttsCd}
                        setSpcAcntgDpstDocId={setSpcAcntgDpstDocId}
                        callTime={callTime}
                    ></BgtSpcDpstMngList>
                </div>
            </div>
        </div>
    );
};

export default BgtSpcDpstMng;
